// docs/116 CI5: the dice table's one sound owner. Mount it once (the inn's layer) for as long as the
// player is in the room; it renders nothing, and plays every throw's clatter, the chips and coins and
// the room's cheers exactly once per event, however many views of the table are drawn.
import { useEffect, useRef } from 'react';
import { useReducedMotion } from '../../../components';
import { dieTiming } from './diceMotion';
import { playInn } from './diceSounds';
import { reactionOf } from './reactions';
import type { DiceView } from './types';

const panOf = (seat: number) => (seat < 0 ? 0 : -0.6 + 0.4 * seat);

/** Plays the sounds of what changed between two views (exported for the harness and tests). */
export function soundsFor(prev: DiceView, next: DiceView, reduced: boolean): void {
  const pan = panOf(next.turn);
  const mine = next.turn >= 0 && next.seats[next.turn]?.mine;
  if (next.throwN !== prev.throwN) {
    const seed = next.throwN;
    playInn('inn.dice_throw', seed, { pan, gain: mine ? 1 : 0.8 });
    for (let i = 0; i < 3; i++) {
      if (!(next.thrown & (1 << i))) continue;
      const t = dieTiming(next.throwN, i);
      const dpan = Math.max(-1, Math.min(1, pan * 0.5 + (i - 1) * 0.25));
      if (reduced) { if (i === 0) playInn('inn.dice_settle', seed + 7, { pan: dpan, delayMs: 80 }); }
      else {
        playInn('inn.dice_settle', seed * 3 + i, { pan: dpan, gain: 0.6, delayMs: t.hits[0] });
        playInn('inn.dice_settle', seed * 5 + i, { pan: dpan, gain: 0.3, delayMs: t.hits[2] });
      }
      if (next.dice[i] === 1) playInn('inn.dice_one', seed * 7 + i, { pan: dpan, gain: 0.8, delayMs: reduced ? 120 : t.end });
    }
  }
  if (next.gameN !== prev.gameN && next.state === 'playing') {
    playInn('inn.chips', next.gameN, { gain: 0.9 });
    playInn('inn.coins_pot', next.gameN, { delayMs: 380 });
  }
  if (next.seats.filter((s) => s.kind === 'hero').length > prev.seats.filter((s) => s.kind === 'hero').length) {
    playInn('inn.chips', next.gameN * 31 + next.seats.length, { gain: 0.45 });
  }
  if (next.state === 'playing' && next.turn >= 0 && next.turn !== prev.turn && next.seats[next.turn]?.state === 'playing') {
    playInn('inn.turn', next.gameN * 4 + next.turn, { gain: mine ? 0.9 : 0.35, pan });
  }
  const r = reactionOf(prev, next);
  if (!r) return;
  const d = reduced ? Math.min(r.delayMs, 150) : r.delayMs;
  if (r.ooh) playInn('inn.gasp', r.seed + 1, { gain: 0.4, delayMs: 120 });
  switch (r.kind) {
    case 'cheer':
      playInn('inn.cheers', r.seed, { delayMs: d + 60, gain: r.big ? 1 : 0.75 });
      if (r.big) playInn('inn.applause', r.seed, { delayMs: d + 500 });
      break;
    case 'gasp': playInn('inn.gasp', r.seed, { delayMs: d + 40 }); break;
    case 'groan': playInn('inn.groan', r.seed, { delayMs: d + 80, gain: 0.8 }); break;
    case 'laugh': playInn('inn.laugh', r.seed, { delayMs: d + 80, gain: r.big ? 1 : 0.75 }); break;
    case 'phew': playInn('inn.clink', r.seed, { delayMs: d + 120, gain: 0.5 }); break;
    case 'win':
      playInn('inn.coins_win', r.seed, { delayMs: 250 });
      playInn('inn.cheers', r.seed + 3, { delayMs: 150, gain: 0.7 });
      if (r.big) playInn('inn.applause', r.seed, { delayMs: 700 });
      playInn('inn.clink', r.seed, { delayMs: 1400, gain: 0.7 });
      break;
    case 'kitty':
      playInn('inn.coin_flip', r.seed, { delayMs: 150 });
      playInn('inn.groan', r.seed, { delayMs: 300, gain: 0.6 });
      break;
  }
}

/** The single owner of the dice table's sounds (renders nothing). */
export function DiceDirector({ view }: { view?: DiceView }) {
  const prev = useRef<DiceView | undefined>(undefined);
  const reduced = useReducedMotion();
  useEffect(() => {
    if (!view) return;
    const p = prev.current;
    prev.current = view;
    if (p) soundsFor(p, view, reduced);
  }, [view, reduced]);
  return null;
}
